
import Vue from "vue";
import { watercraftTypeTableColumns } from "./WatercraftTypeTable";
import { ITableWrapperColumns } from "@/types";
import NoResultsFound from "@/components/NotFound/NoResultsFound.vue";
import {
  watercraftTypeMapActions,
  watercraftTypeMapGetters
} from "@/store/modules/watercraftType";
import { cloneDeep } from "lodash";

interface IOptions {
  label: string;
  faIcon: string;
  class?: string;
  value: string;
}

interface IWatercraftTypeData {
  tableActions: any[];
  errorMessage: string;
  isLoading: boolean;
  isAdmin: boolean;
  loading: boolean;
  successMessage: string;
  watercraftTypeId: string;
  loadingText: string;
  selectedDocuments: any[];
  queryOverride: { deleted: boolean };
}

export default Vue.extend({
  name: "ListWatercraftTypes",
  components: {
    NoResultsFound
  },
  data(): IWatercraftTypeData {
    return {
      successMessage: "",
      errorMessage: "",
      isLoading: false,
      isAdmin: true,
      tableActions: [],
      loading: false,
      watercraftTypeId: "",
      loadingText: "",
      selectedDocuments: [],
      queryOverride: { deleted: false }
    };
  },
  methods: {
    ...watercraftTypeMapActions([
      "getWatercraftTypes",
      "deleteWatercraftType",
      "deleteBulkWatercraftTypes",
      "restoreBulkWatercraftTypes"
    ]),
    onToolbarItemClick(event: string) {
      switch (event) {
        case "createWatercraftType":
          this.$router.push("/admin/watercraftType/create");
          break;
        case "watercraftTypes-recycle-bin":
          this.queryOverride = { deleted: true };
          this.selectedDocuments = [];
          this.fetchPageData({});
          break;
        case "watercraftTypes":
          this.queryOverride = { deleted: false };
          this.selectedDocuments = [];
          this.fetchPageData({});
          break;
        case "watercraftTypes-activities":
          this.$router
            .push(`/admin/watercraftTypes/activities`)
            .catch(() => {});
          break;
      }
    },
    async fetchPageData(queryObject: any): Promise<any> {
      try {
        this.loading = true;
        this.loadingText = "Loading watercraft types. Please wait...";
        this.$emit("scrollTop");
        await this.getWatercraftTypes({
          query: { ...queryObject, ...this.queryOverride }
        });
      } catch (error) {
        this.errorMessage = error.message;
        this.$bugSnagClient.notify(error);
      } finally {
        this.loading = false;
        this.loadingText = "";
      }
    },
    async viewCallback(dataReceived: any): Promise<any> {
      if (this.viewingRecycleBin) return;
      const { _id } = dataReceived;
      this.$router.push(`/admin/${_id}/watercraftType`).catch(() => {});
    },
    async handleResponseAction(payload: any) {
      const { data } = payload;
      switch (payload.name) {
        case "delete":
          this.deleteCallback(data._id);
          break;
        case "restore":
          this.selectedDocuments = [data];
          this.$modal.show("watercraftTypesBulkRestoreModal");
          break;
        case "edit":
          this.$router.push(`/admin/${data._id}/watercraftType`);
          break;
        default:
          this.errorMessage = "Operation specified is not supported";
      }
    },
    bulkActionHandler(action: string) {
      switch (action) {
        case "Delete Selected":
          if (!this.selectedDocuments.length) {
            this.errorMessage = "No watercraft types selected.";
            return;
          }
          this.$modal.show("watercraftTypesBulkDeleteModal");
          break;
        case "Restore Selected":
          if (!this.selectedDocuments.length) {
            this.errorMessage = "No watercraft types selected.";
            return;
          }
          this.$modal.show("watercraftTypesBulkRestoreModal");
          break;
      }
    },
    async doDeleteBulkWatercraftTypes(): Promise<void> {
      try {
        this.loadingText = "Deleting selected watercraft types. Please wait...";
        const watercraftTypeIds = this.selectedDocuments.map(
          (doc: any) => doc._id
        );
        await this.deleteBulkWatercraftTypes(watercraftTypeIds);
        this.$appNotifySuccess(
          "Selected watercraft types deleted successfully."
        );
        this.selectedDocuments = [];
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.$modal.hide("watercraftTypesBulkDeleteModal");
        this.loadingText = "";
      }
    },
    async doRestoreBulkWatercraftTypes(): Promise<void> {
      try {
        this.loadingText =
          "Restoring selected watercraft types. Please wait...";
        const watercraftTypeIds = this.selectedDocuments.map(
          (doc: any) => doc._id
        );
        await this.restoreBulkWatercraftTypes(watercraftTypeIds);
        this.$appNotifySuccess(
          "Selected watercraft types restored successfully."
        );
        this.selectedDocuments = [];
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.$modal.hide("watercraftTypesBulkRestoreModal");
        this.loadingText = "";
      }
    },
    async deleteOneWatercraftType(): Promise<void> {
      try {
        this.isLoading = true;
        this.loadingText = "Deleting watercraft type. Please wait...";
        await this.deleteWatercraftType(this.watercraftTypeId);
        this.$modal.hide("watercraftTypeDeleteModal");
        this.$appNotifySuccess("Watercraft Type Successfully Deleted");
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.isLoading = false;
        this.loadingText = "";
      }
    },
    deleteCallback(id: string): void {
      this.watercraftTypeId = id;
      this.$modal.show("watercraftTypeDeleteModal");
    }
  },
  computed: {
    ...watercraftTypeMapGetters([
      "watercraftTypes",
      "getTotalRecords",
      "getWatercraftTypesLinks"
    ]),
    tableColumns(): ITableWrapperColumns {
      return cloneDeep(watercraftTypeTableColumns);
    },
    viewingRecycleBin(): boolean {
      return this.queryOverride.deleted === true;
    },
    tableRowActions(): IOptions[] {
      if (this.viewingRecycleBin) {
        const actions: IOptions[] = [];
        if (this.$isCurrentUserAdmin) {
          actions.push({
            label: "Restore",
            value: "restore",
            faIcon: "trash-restore",
            class: "bg-blue-400 text-white hover:text-white hover:bg-blue-500"
          });
        }
        return actions;
      }

      const actions: IOptions[] = [
        { label: "Edit", value: "edit", faIcon: "edit" }
      ];

      if (this.$isCurrentUserAdmin) {
        actions.push({
          label: "Delete",
          value: "delete",
          faIcon: "trash-alt",
          class: "bg-red-400 text-white hover:text-white hover:bg-red-500"
        });
      }

      return actions;
    },
    bulkMenuActions(): { title: string; disabled: boolean }[] {
      if (!this.$isCurrentUserAdmin) return [];
      return [
        {
          title: this.viewingRecycleBin
            ? "Restore Selected"
            : "Delete Selected",
          disabled: false
        }
      ];
    }
  },
  mounted() {
    this.fetchPageData({});
  }
});
